/**
 * IncidentsView - View for listing incidents for a job
 * Epic 6: Quality/Incidents/Exceptions
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { Add, ArrowBack } from "@mui/icons-material";
import { getIncidentsByJobId, createIncident } from "../services/incidentService";
import IncidentCard from "../components/IncidentCard";
import IncidentForm from "../components/IncidentForm";

/**
 * IncidentsView Component
 */
const IncidentsView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchIncidents = useCallback(async () => {
    if (!orderId) return;

    try {
      setLoading(true);
      setError(null);
      const result = await getIncidentsByJobId(orderId);
      if (result.success) {
        setIncidents(result.data || []);
      } else {
        setError(result.error || "Failed to load incidents");
      }
    } catch (err) {
      setError(err.message || "Failed to load incidents");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const handleCreateIncident = async (incidentData) => {
    try {
      const result = await createIncident(orderId, incidentData);
      if (result.success) {
        setNotification({
          open: true,
          message: "Incident created successfully",
          severity: "success",
        });
        setFormOpen(false);
        fetchIncidents();
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to create incident",
          severity: "error",
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to create incident",
        severity: "error",
      });
    }
  };

  const handleIncidentClick = (incident) => {
    navigate(`/orders/${orderId}/incidents/${incident.id}`);
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/orders/${orderId}`)}
          >
            Back to Order
          </Button>
          <Typography variant="h4" component="h1">
            Incidents
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setFormOpen(true)}
        >
          Create Incident
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : incidents.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No incidents found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create an incident to track quality issues for this order
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setFormOpen(true)}
          >
            Create First Incident
          </Button>
        </Box>
      ) : (
        <Box>
          {incidents.map((incident) => (
            <IncidentCard
              key={incident.id}
              incident={incident}
              onClick={handleIncidentClick}
            />
          ))}
        </Box>
      )}

      <IncidentForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreateIncident}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default IncidentsView;
